import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface MarketSnapshot {
  article_slug: string;
  market_slug: string;
  market_question: string;
  delta_points: number;
}

/**
 * Fetches market snapshots where the absolute delta is >= threshold points.
 * Returns a Map keyed by article_slug for O(1) lookup.
 */
export function useMarketMovers(threshold = 3) {
  return useQuery({
    queryKey: ["market-movers", threshold],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("market_snapshots")
        .select("article_slug, market_slug, market_question, delta_points")
        .or(`delta_points.gte.${threshold},delta_points.lte.${-threshold}`);

      if (error) throw error;

      const map = new Map<string, MarketSnapshot>();
      for (const row of data ?? []) {
        const existing = map.get(row.article_slug);
        // Keep the largest absolute move per article
        if (!existing || Math.abs(row.delta_points) > Math.abs(existing.delta_points)) {
          map.set(row.article_slug, row as MarketSnapshot);
        }
      }
      return map;
    },
    staleTime: 5 * 60 * 1000,
    structuralSharing: true,
  });
}
